/**
 * DocumentHeader — Centered branded banner + custom document-name input.
 *
 * Drop-in component shown above every printable/saveable document
 * (OS&E lists, schedules, permits, insurer register, subcontractors,
 * checklists, …). Behaviour:
 *
 *   • Centered banner above the name box:
 *       - Hotel logo (always)
 *       - Restaurant / department logo (when an outlet/dept is in scope)
 *       - Hotel name + subtitle + restaurant chip
 *
 *   • Document-name input:
 *       - Pre-filled with an auto-generated default (caller passes `autoName`).
 *       - User can override; the typed name is persisted via
 *         PUT /api/admin/document-titles/{scopeId}.
 *       - On change, parent receives the effective title via `onChange`.
 *       - The effective title is also shown on the saved snapshot / export
 *         / print (caller wires this through).
 *
 *   • Persistence is transparent: GET on mount, PUT on blur/Enter (debounced
 *     by the blur/Enter event itself).
 *
 *   • Resilient to missing branding: if the property fetch fails we show
 *     "Dreams Rose Hall Resort & Spa" as a friendly default.
 */
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const absUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  return `${BACKEND}${u}`;
};

export default function DocumentHeader({
  scopeId,           // unique persistence key (e.g. "permits", "schedule.dept.2026-02")
  autoName,          // auto-generated default name shown when no override exists
  outlet,            // optional restaurant outlet ({ name, logo_url, accent_color })
  deptLabel,         // optional department label (e.g. "Engineering")
  onChange,          // (effectiveTitle: string) => void — fires on every name change
  readOnly = false,  // disable persistence (viewer mode)
  compact = false,   // smaller padding for embedded scenarios
}) {
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const [property, setProperty] = useState(null);
  const [draft, setDraft] = useState('');     // live input value
  const [saved, setSaved] = useState('');     // persisted name on the backend
  const [loaded, setLoaded] = useState(false);
  const lastEmittedRef = useRef('');

  // Effective title = override (if non-empty) else autoName
  const effective = (draft || '').trim() || autoName || 'Untitled document';

  // Emit on every change so the parent can flow it into exports/snapshot defaults.
  useEffect(() => {
    if (lastEmittedRef.current !== effective) {
      lastEmittedRef.current = effective;
      onChange?.(effective);
    }
  }, [effective, onChange]);

  // Fetch hotel branding (once).
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/property`).then(({ data }) => {
      if (!cancelled) setProperty(data || {});
    }).catch(() => { if (!cancelled) setProperty({}); });
    return () => { cancelled = true; };
  }, []);

  // Fetch persisted title for this scope (once per scopeId).
  useEffect(() => {
    if (!scopeId) { setLoaded(true); return; }
    let cancelled = false;
    axios.get(`${API}/document-titles/${encodeURIComponent(scopeId)}`)
      .then(({ data }) => {
        if (cancelled) return;
        const t = (data?.title || '').trim();
        setSaved(t);
        setDraft(t);
        setLoaded(true);
      })
      .catch(() => { if (!cancelled) { setSaved(''); setDraft(''); setLoaded(true); } });
  }, [scopeId]);

  const commit = async () => {
    if (readOnly || !adminToken || !scopeId) return;
    const next = (draft || '').trim();
    if (next === saved) return;
    try {
      await axios.put(
        `${API}/admin/document-titles/${encodeURIComponent(scopeId)}`,
        { title: next },
        { headers: { Authorization: `Bearer ${adminToken}` } },
      );
      setSaved(next);
    } catch (_e) {
      // Restore on failure
      setDraft(saved);
    }
  };

  const accent = outlet?.accent_color || '#003DA5';
  const hotelName = property?.title || 'Dreams Rose Hall Resort & Spa';
  const hotelSubtitle = property?.subtitle || '';
  const hotelLogo = absUrl(property?.brand_logo_url || '');
  const restaurantName = outlet?.name || '';
  const restaurantLogo = absUrl(outlet?.logo_url || '');

  return (
    <div
      data-testid="document-header"
      className={`mb-4 rounded-2xl overflow-hidden ${compact ? '' : ''}`}
      style={{
        background: `linear-gradient(135deg, ${accent}12 0%, ${accent}04 100%)`,
        border: `1px solid ${accent}30`,
      }}
    >
      {/* CENTERED BRANDING BANNER — hotel logo + name (centered),
          restaurant logo + name on the right when scoped. */}
      <div
        className={`flex flex-col items-center text-center gap-2 ${compact ? 'px-4 py-3' : 'px-5 py-4'}`}
        style={{ borderBottom: `1px solid ${accent}20` }}
      >
        <div className="flex items-center justify-center gap-4 flex-wrap">
          {hotelLogo && (
            <img
              src={hotelLogo}
              alt={hotelName}
              className="h-12 w-auto max-w-[140px] object-contain rounded-md"
              style={{ background: 'white', padding: 4, border: `1px solid ${accent}20` }}
              data-testid="document-header-hotel-logo"
            />
          )}
          {restaurantLogo && (
            <img
              src={restaurantLogo}
              alt={restaurantName}
              className="h-12 w-12 object-contain rounded-lg"
              style={{ background: 'white', padding: 4, border: `1px solid ${accent}40` }}
              data-testid="document-header-restaurant-logo"
            />
          )}
        </div>
        <div className="min-w-0 max-w-full">
          <p
            className="text-[10px] font-bold tracking-[0.3em] uppercase"
            style={{ color: accent, fontFamily: 'Manrope' }}
          >
            {hotelName}
          </p>
          {hotelSubtitle && (
            <p className="text-xs mt-0.5" style={{ color: '#64748B', fontFamily: 'Manrope' }}>
              {hotelSubtitle}
            </p>
          )}
          {(restaurantName || deptLabel) && (
            <p
              className="text-base font-bold mt-1.5 truncate"
              style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}
              data-testid="document-header-scope-label"
            >
              {restaurantName || deptLabel}
            </p>
          )}
        </div>
      </div>

      {/* Document-name input — pre-filled with autoName, persists on blur/Enter. */}
      <div className={`flex flex-col sm:flex-row sm:items-center gap-2 ${compact ? 'px-4 py-2.5' : 'px-5 py-3'}`}>
        <label
          className="text-[10px] font-bold tracking-[0.25em] uppercase flex-shrink-0"
          style={{ color: accent, fontFamily: 'Manrope' }}
          htmlFor={`doc-name-${scopeId || 'na'}`}
        >
          Document name
        </label>
        <input
          id={`doc-name-${scopeId || 'na'}`}
          type="text"
          value={loaded ? draft : ''}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') { setDraft(saved); e.currentTarget.blur(); }
          }}
          placeholder={autoName || 'Untitled document'}
          disabled={readOnly || !adminToken}
          data-testid="document-header-name-input"
          className="flex-1 px-3 py-2 rounded-lg text-base font-bold outline-none transition-colors min-w-0"
          style={{
            background: 'white',
            color: '#1A2E2E',
            border: `1px solid ${accent}40`,
            fontFamily: "'Cormorant Garamond', serif",
          }}
        />
        {!readOnly && adminToken && (
          <span className="text-[10px]" style={{ color: '#64748B', fontFamily: 'Manrope' }}>
            press Enter to save
          </span>
        )}
      </div>
    </div>
  );
}
